import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import socket from "../../utils/socketConnect.utils";
import CampaignMessageDocument from "../../models/whatsappCampaigns/campaignMsgs.model";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { fetchMsgs } from "../../store/slices/whatsappCampaignSlice";

export default function CampaignMessagesPage() {
  const dispatch: RootDispatch = useDispatch();
  const { campaignId } = useParams();

  const loading = useSelector(
    (state: RootState) => state.whatsappCampaign.loading.fetchMsgs,
  );

  const [messages, setMessages] = useState<CampaignMessageDocument[]>([]);
  // Pagination & Search states
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch API
  const fetchMessages = useCallback(async () => {
    try {
      const res = await dispatch(
        fetchMsgs({
          campaignId: campaignId!,
          page,
          limit: 10,
          search: search.trim(),
        }),
      ).unwrap();
      if (res.data && Array.isArray(res.data)) {
        setMessages(res.data);
      } else {
        setMessages([]);
      }
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [campaignId, dispatch, page, search]);

  // Initial and reactive data fetching
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Socket Listener Setup
  useEffect(() => {
    if (!campaignId) return;

    socket.emit("join-campaign", campaignId);

    const handleMessageUpdate = (updatedMsg: CampaignMessageDocument) => {
      setMessages((prevMsgs) => {
        if (search.trim() && !updatedMsg.mobileNumber.includes(search.trim())) {
          return prevMsgs;
        }

        const filtered = prevMsgs.filter((msg) => msg._id !== updatedMsg._id);
        return [updatedMsg, ...filtered];
      });
    };

    socket.on("campaign-msg-updated", handleMessageUpdate);

    return () => {
      socket.emit("leave-campaign", campaignId);
      socket.off("campaign-msg-updated", handleMessageUpdate);
    };
  }, [campaignId, search]);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-lg font-semibold">Campaign Messages</h3>

        <div className="w-full sm:w-64">
          <label className="sr-only">Search mobile</label>
          <input
            type="text"
            placeholder="Search mobile number..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600" />
            <span className="ml-3 text-sm text-gray-600">Loading...</span>
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="space-y-3 sm:hidden">
              {messages.length > 0 ? (
                messages.map((msg) => (
                  <div
                    key={msg._id}
                    className="border rounded-lg p-3 bg-white shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {msg.mobileNumber}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Attempts: {msg.attempts}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="inline-block px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700">
                          {msg.status}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {msg.updatedAt
                            ? new Date(msg.updatedAt).toLocaleString()
                            : "-"}
                        </div>
                      </div>
                    </div>
                    {msg.error ? (
                      <div className="mt-2 text-xs text-red-600">
                        Error: {msg.error}
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="py-6 text-center text-sm text-gray-500">
                  No messages found
                </div>
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-100">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-4 py-2">Mobile</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Attempts</th>
                    <th className="px-4 py-2">Last Sent</th>
                    <th className="px-4 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.length > 0 ? (
                    messages.map((msg) => (
                      <tr
                        key={msg._id}
                        className="border-t last:border-b hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {msg.mobileNumber}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {msg.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {msg.attempts}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {msg.updatedAt
                            ? new Date(msg.updatedAt).toLocaleString()
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-red-600">
                          {msg.error || "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-sm text-gray-500"
                      >
                        No messages found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className={`px-3 py-2 rounded-md text-sm bg-white border ${page <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"}`}
        >
          Previous
        </button>

        <div className="text-sm text-gray-700">
          Page {page} of {totalPages}
        </div>

        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className={`px-3 py-2 rounded-md text-sm bg-indigo-600 text-white ${page >= totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-indigo-700"}`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
